import {
  InsertDriveFile,
  Image as ImageIcon,
  PictureAsPdf,
  Close as CloseIcon,
  Download as DownloadIcon
} from "@mui/icons-material";
import {
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Stack
} from "@mui/material";
import axiosInstance from "../../../utils/axios";
import { useEffect, useRef, useState } from "react";

const DocumentModal = ({ open, onClose, document: doc }) => {
  const [loading, setLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [error, setError] = useState(null);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const imageRef = useRef(null);

  const handleDownload = async () => {
    if (!doc?.upload_id) return;

    setLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.get(
        `/file_upload/download?image_id=${doc.upload_id}`
      );

      if (response?.data) {
        setImageUrl(response?.data);
      }
    } catch (error) {
      console.error('Download error:', error);
      setError('Failed to load document. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDirectDownload = async () => {
    try {
      setDownloadLoading(true);
      const link = window.document.createElement("a");
      link.href = imageUrl;
      link.setAttribute("download", doc.name || "downloaded_file");
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
    } catch (error) {
      console.error("Download failed:", error);
      setError("Failed to download file.");
    } finally {
      setDownloadLoading(false);
    }
  };

  useEffect(() => {
    if (doc && open) {
      handleDownload();
    }
    // eslint-disable-next-line
  }, [doc, open]);

  useEffect(() => {
    if (!open) {
      setImageUrl(null);
      setError(null);
      setLoading(false);
      setDownloadLoading(false);
    }
  }, [open]);

  const handleClose = () => {
    setImageUrl(null);
    setError(null);
    setLoading(false);
    setDownloadLoading(false);
    onClose();
  };

  const getFileIcon = (fileType) => {
    if (fileType?.includes("pdf")) {
      return <PictureAsPdf sx={{ color: "#d32f2f", fontSize: 40 }} />;
    } else if (fileType?.includes("image")) {
      return <ImageIcon sx={{ color: "#1976d2", fontSize: 40 }} />;
    }
    return <InsertDriveFile sx={{ color: "#757575", fontSize: 40 }} />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const isImageFile = (fileType, fileName) => {
    if (fileType?.includes("image")) return true;
    if (fileName) {
      const extension = fileName.toLowerCase().split(".").pop();
      return ["png", "jpg", "jpeg", "gif", "bmp", "webp"].includes(extension);
    }
    return false;
  };

  const isPdfFile = (fileType, fileName) => {
    if (fileType?.includes("pdf")) return true;
    if (fileName) {
      const extension = fileName.toLowerCase().split(".").pop();
      return extension === "pdf";
    }
    return false;
  };

  const currentFileIsImage = isImageFile(doc?.type, doc?.name);
  const currentFileIsPdf = isPdfFile(doc?.type, doc?.name);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: "500px", maxHeight: "90vh" },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Typography variant="h6">Document Viewer</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDirectDownload}
            disabled={downloadLoading || loading || !imageUrl}
            size="small"
          >
            {downloadLoading ? "Downloading..." : "Download"}
          </Button>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {doc && (
          <Box>
            <Box sx={{ mb: 3, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                {getFileIcon(doc.type)}
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    {doc.name}
                  </Typography>
                  <Stack direction="row" spacing={1} mt={1}>
                    <Chip
                      label={formatFileSize(doc.size)}
                      size="small"
                      variant="outlined"
                    />
                    {doc.type && (
                      <Chip
                        label={
                          doc.type.split("/")[1]?.toUpperCase() || "FILE"
                        }
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                    <Chip
                      label={`ID: ${doc.upload_id}`}
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  </Stack>
                </Box>
              </Stack>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {loading && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  py: 4,
                  height: "400px",
                  justifyContent: "center",
                }}
              >
                <CircularProgress size={40} />
                <Typography variant="body2" color="textSecondary" mt={2}>
                  Loading document...
                </Typography>
              </Box>
            )}

            {!loading && (
              <>
                {imageUrl && currentFileIsImage && (
                  <Box sx={{ textAlign: "center", mt: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        p: 1,
                        bgcolor: "grey.50",
                        maxHeight: "500px",
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        component="img"
                        ref={imageRef}
                        src={imageUrl}
                        alt={doc.name}
                        sx={{
                          width: "600px",
                          height: "400px",
                          objectFit: "contain",
                          borderRadius: 1,
                        }}
                        onError={() =>
                          setError("Failed to load image preview")
                        }
                      />
                    </Box>
                  </Box>
                )}

                {imageUrl && currentFileIsPdf && (
                  <Box sx={{ textAlign: "center", mt: 2 }}>
                    <Typography variant="subtitle1" gutterBottom fontWeight={500}>
                      PDF Document:
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        p: 3,
                        bgcolor: "grey.50",
                        minHeight: "300px",
                      }}
                    >
                      <Box sx={{ textAlign: "center" }}>
                        <PictureAsPdf
                          sx={{ fontSize: 80, color: "#d32f2f", mb: 2 }}
                        />
                        <Typography variant="h6" gutterBottom>
                          PDF Document
                        </Typography>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          gutterBottom
                        >
                          {doc.name}
                        </Typography>
                        <Button
                          variant="outlined"
                          startIcon={<DownloadIcon />}
                          onClick={handleDirectDownload}
                          disabled={downloadLoading}
                          sx={{ mt: 2 }}
                        >
                          {downloadLoading
                            ? "Downloading..."
                            : "Download PDF"}
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                )}

                {imageUrl &&
                  !currentFileIsImage &&
                  !currentFileIsPdf && (
                    <Box sx={{ textAlign: "center", py: 4 }}>
                      <Box sx={{ mb: 2 }}>{getFileIcon(doc.type)}</Box>
                      <Typography variant="h6" gutterBottom>
                        {doc.name}
                      </Typography>
                      <Typography
                        variant="body1"
                        color="textSecondary"
                        gutterBottom
                      >
                        This file type cannot be previewed.
                      </Typography>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        mb={2}
                      >
                        Click the download button to save the file.
                      </Typography>
                      <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={handleDirectDownload}
                        disabled={downloadLoading}
                      >
                        {downloadLoading ? "Downloading..." : "Download File"}
                      </Button>
                    </Box>
                  )}

                {!imageUrl && !error && (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography variant="body1" color="textSecondary">
                      No content available for this document.
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DocumentModal;
